import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotate, faStore, faXmark, faCheck, faCircleExclamation,
  faBolt, faHourglassHalf, faPlay, faTrash, faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Utils from "../../../configs/Utils";
import Enums from "../../../configs/Enums";
import { useWooJobs } from "../../../contexts/WooJobsContext";

/**
 * Multi-store WooCommerce orders sync.
 *
 * - Loads connected stores from `/gateway/woo/stores`.
 * - Persists the user's selection in localStorage so revisits remember.
 * - Optional filters: status (multi), since (date), perPage.
 * - Submits `SYNC_WOO_ORDERS` background job and renders inline progress
 *   from `lastJobByType.SYNC_WOO_ORDERS` (provided by WooJobsContext).
 */

const LS_KEY = "woo.sync.selectedStores";
const LS_FILTERS = "woo.sync.filters";

const WOO_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "on-hold", label: "On hold" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "refunded", label: "Refunded" },
  { value: "failed", label: "Failed" },
  { value: "trash", label: "Trash" },
];

const loadJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
};

const saveJSON = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* ignore quota */ }
};

const WooSyncOrders = () => {
  const { lastJobByType, submitJob, cancelJob } = useWooJobs();
  const job = lastJobByType?.SYNC_WOO_ORDERS;

  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set(loadJSON(LS_KEY, [])));
  const [submitting, setSubmitting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteScope, setDeleteScope] = useState("selected"); // "selected" | "all"

  const initialFilters = loadJSON(LS_FILTERS, {
    statuses: [],
    since: "",
    perPage: 50,
  });
  const [statuses, setStatuses] = useState(initialFilters.statuses || []);
  const [since, setSince] = useState(initialFilters.since || "");
  const [perPage, setPerPage] = useState(initialFilters.perPage || 50);

  const isActive = job && (job.status === "queued" || job.status === "running");

  const fetchStores = async () => {
    setStoresLoading(true);
    try {
      const r = await Utils._apiRequest({
        path: Enums.endpoints.woo.STORES,
        type: "GET",
      });
      if (r?.ok) setStores(r.items || []);
      else toast.error(r?.errors || "Không tải được danh sách cửa hàng");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setStoresLoading(false);
    }
  };

  useEffect(() => { fetchStores(); }, []);

  // Persist selection + filters.
  useEffect(() => { saveJSON(LS_KEY, Array.from(selected)); }, [selected]);
  useEffect(() => {
    saveJSON(LS_FILTERS, { statuses, since, perPage });
  }, [statuses, since, perPage]);

  const connectedStores = useMemo(() => stores.filter((s) => s.connected), [stores]);

  const toggleStore = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllConnected = () => {
    setSelected(new Set(connectedStores.map((s) => String(s._id))));
  };
  const clearSelection = () => setSelected(new Set());

  const toggleStatus = (val) => {
    setStatuses((prev) => prev.includes(val)
      ? prev.filter((v) => v !== val)
      : [...prev, val]);
  };

  const handleSubmit = async () => {
    const ids = Array.from(selected).filter((id) =>
      connectedStores.some((s) => String(s._id) === id),
    );
    if (!ids.length) {
      toast.error("Vui lòng chọn ít nhất 1 cửa hàng đã kết nối");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        storeIds: ids,
        perPage: Math.min(Math.max(Number(perPage) || 50, 1), 100),
      };
      if (statuses.length) payload.status = statuses;
      if (since) payload.since = since;
      const label = `Đồng bộ đơn hàng • ${ids.length} cửa hàng`;
      await submitJob("SYNC_WOO_ORDERS", payload, label);
      toast.success("Đã đưa job vào hàng đợi");
    } catch (err) {
      // Error toast already raised by context.
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!job?._id) return;
    try {
      await cancelJob(job._id);
      toast.success("Đã yêu cầu hủy");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openDeleteModal = () => {
    setDeleteConfirm("");
    setDeleteScope(selected.size > 0 ? "selected" : "all");
    setShowDelete(true);
  };

  const handleDeleteAll = async () => {
    if (deleteConfirm !== "DELETE") {
      toast.error('Gõ "DELETE" để xác nhận');
      return;
    }
    if (isActive) {
      toast.error("Vui lòng huỷ job đang chạy trước khi xoá");
      return;
    }
    setDeleting(true);
    try {
      const body = { confirm: "DELETE" };
      if (deleteScope === "selected") {
        const ids = Array.from(selected);
        if (!ids.length) {
          toast.error("Chưa chọn cửa hàng nào");
          setDeleting(false);
          return;
        }
        body.storeIds = ids;
      }
      const r = await Utils._apiRequest({
        path: Enums.endpoints.woo.ORDERS_DELETE_ALL,
        type: "POST",
        payload: body,
      });
      if (!r?.ok) throw new Error(r?.errors || "Xoá thất bại");
      toast.success(`Đã xoá ${r.deleted || 0} đơn hàng`);
      setShowDelete(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const renderProgress = () => {
    if (!job) return null;
    const total = job.progress?.total || 0;
    const current = job.progress?.current || 0;
    const pct = total > 0
      ? Math.min(100, Math.round((current / total) * 100))
      : (job.status === "running" ? 100 : 0);
    const variant = job.status === "error" ? "danger"
      : job.status === "cancelled" ? "warning"
      : job.status === "done" ? "success"
      : undefined;
    const meta = {
      queued: { icon: faHourglassHalf, color: "secondary", label: "Hàng đợi" },
      running: { icon: faBolt, color: "primary", label: "Đang chạy" },
      done: { icon: faCheck, color: "success", label: "Hoàn tất" },
      error: { icon: faCircleExclamation, color: "danger", label: "Lỗi" },
      cancelled: { icon: faXmark, color: "warning", label: "Đã hủy" },
    }[job.status] || { icon: faBolt, color: "secondary", label: job.status };

    return (
      <div className="border rounded p-3 mt-3 bg-light-subtle">
        <div className="d-flex align-items-center gap-2 mb-2">
          <Badge bg={meta.color} className="d-inline-flex align-items-center gap-1">
            <FontAwesomeIcon icon={meta.icon} />{meta.label}
          </Badge>
          {job.label && <span className="text-muted small">{job.label}</span>}
          {isActive && (
            <Button
              size="sm"
              variant="outline-warning"
              className="ms-auto"
              onClick={handleCancel}
            >
              <FontAwesomeIcon icon={faXmark} className="me-1" />Hủy
            </Button>
          )}
        </div>
        <ProgressBar
          now={pct}
          label={total > 0 ? `${pct}%` : ""}
          striped={isActive}
          animated={job.status === "running"}
          variant={variant}
          style={{ height: 16 }}
        />
        <div className="small text-muted mt-1">
          {total > 0 && <span className="me-2">{current.toLocaleString()} / {total.toLocaleString()} cửa hàng</span>}
          {job.progress?.message || ""}
        </div>
        {job.progress?.extra && (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {typeof job.progress.extra.processed === "number" && (
              <Badge bg="light" text="dark" className="border">
                <span className="text-muted">đã xử lý:</span> {job.progress.extra.processed.toLocaleString()}
              </Badge>
            )}
            {typeof job.progress.extra.inserted === "number" && (
              <Badge bg="success">+{job.progress.extra.inserted} mới</Badge>
            )}
            {typeof job.progress.extra.updated === "number" && (
              <Badge bg="info">{job.progress.extra.updated} cập nhật</Badge>
            )}
            {typeof job.progress.extra.failed === "number" && job.progress.extra.failed > 0 && (
              <Badge bg="danger">{job.progress.extra.failed} thất bại</Badge>
            )}
          </div>
        )}
        {job.error && (
          <Alert variant="danger" className="mt-2 mb-0 py-2 small">
            {job.error}
          </Alert>
        )}
      </div>
    );
  };

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header className="bg-white d-flex align-items-center gap-2 py-3">
        <FontAwesomeIcon icon={faStore} className="text-primary" />
        <strong>Đồng bộ đơn hàng WooCommerce</strong>
        <span className="text-muted small ms-2">
          {connectedStores.length}/{stores.length} cửa hàng đã kết nối
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          className="ms-auto"
          onClick={fetchStores}
          disabled={storesLoading}
          title="Tải lại danh sách cửa hàng"
        >
          {storesLoading ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faRotate} />}
        </Button>
      </Card.Header>

      <Card.Body>
        <Row className="g-3">
          <Col md={7}>
            <div className="d-flex align-items-center mb-2">
              <strong className="small text-uppercase text-muted">Chọn cửa hàng</strong>
              <div className="ms-auto d-flex gap-2">
                <Button size="sm" variant="link" className="p-0" onClick={selectAllConnected}>
                  Chọn tất cả
                </Button>
                <span className="text-muted">|</span>
                <Button size="sm" variant="link" className="p-0 text-muted" onClick={clearSelection}>
                  Bỏ chọn
                </Button>
              </div>
            </div>
            <div
              className="border rounded p-2"
              style={{ maxHeight: 280, overflowY: "auto" }}
            >
              {storesLoading && (
                <div className="text-center text-muted py-3">
                  <Spinner size="sm" animation="border" /> Đang tải…
                </div>
              )}
              {!storesLoading && stores.length === 0 && (
                <div className="text-center text-muted py-3 small">
                  Chưa có cửa hàng WooCommerce nào.
                </div>
              )}
              {!storesLoading && stores.map((s) => {
                const id = String(s._id);
                const checked = selected.has(id);
                return (
                  <Form.Check
                    key={id}
                    type="checkbox"
                    id={`woo-store-${id}`}
                    className="py-1 d-flex align-items-center"
                    checked={checked}
                    onChange={() => toggleStore(id)}
                    disabled={!s.connected}
                    label={(
                      <span className="d-inline-flex align-items-center gap-2">
                        <span className="fw-semibold">{s.storeFriendlyName || s.storeName || s.domain}</span>
                        <span className="text-muted small">{s.domain}</span>
                        {s.connected ? (
                          <Badge bg="success-subtle" text="success" className="border border-success-subtle">
                            <FontAwesomeIcon icon={faCheck} className="me-1" />kết nối
                          </Badge>
                        ) : (
                          <Badge bg="secondary-subtle" text="secondary" className="border border-secondary-subtle">
                            chưa kết nối
                          </Badge>
                        )}
                      </span>
                    )}
                  />
                );
              })}
            </div>
            <div className="small text-muted mt-1">
              Đã chọn {Array.from(selected).filter((id) =>
                connectedStores.some((s) => String(s._id) === id),
              ).length} cửa hàng đã kết nối
            </div>
          </Col>

          <Col md={5}>
            <div className="mb-2">
              <strong className="small text-uppercase text-muted">Bộ lọc</strong>
            </div>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-1">Trạng thái đơn (tuỳ chọn)</Form.Label>
              <div className="d-flex flex-wrap gap-1">
                {WOO_STATUSES.map((s) => {
                  const active = statuses.includes(s.value);
                  return (
                    <Button
                      key={s.value}
                      size="sm"
                      variant={active ? "primary" : "outline-secondary"}
                      onClick={() => toggleStatus(s.value)}
                    >
                      {s.label}
                    </Button>
                  );
                })}
              </div>
              <Form.Text className="text-muted">
                Bỏ trống = đồng bộ tất cả trạng thái.
              </Form.Text>
            </Form.Group>

            <Row>
              <Col xs={7}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Từ ngày (tuỳ chọn)</Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={since}
                    onChange={(e) => setSince(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={5}>
                <Form.Group className="mb-2">
                  <Form.Label className="small mb-1">Per page</Form.Label>
                  <Form.Control
                    type="number"
                    size="sm"
                    min={1}
                    max={100}
                    value={perPage}
                    onChange={(e) => setPerPage(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-grid gap-2 mt-2">
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={submitting || isActive || selected.size === 0}
              >
                {submitting || isActive ? (
                  <><Spinner size="sm" animation="border" className="me-2" />
                    {isActive ? "Đang chạy…" : "Đang gửi…"}
                  </>
                ) : (
                  <><FontAwesomeIcon icon={faPlay} className="me-2" />Bắt đầu đồng bộ</>
                )}
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={openDeleteModal}
                disabled={isActive || deleting}
                title="Xoá toàn bộ đơn hàng trong DB để đồng bộ lại từ đầu"
              >
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Xoá đơn trong DB để sync lại…
              </Button>
            </div>
          </Col>
        </Row>

        {renderProgress()}
      </Card.Body>

      <Modal show={showDelete} onHide={() => !deleting && setShowDelete(false)} centered backdrop="static">
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="d-flex align-items-center gap-2 text-danger">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            Xoá đơn hàng trong DB
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="small mb-3">
            Hành động này sẽ <strong>xoá vĩnh viễn</strong> các đơn hàng WooCommerce
            đã đồng bộ trong cơ sở dữ liệu của bạn. Sau đó bạn có thể chạy đồng bộ lại
            từ đầu để lấy dữ liệu mới từ Woo. <strong>Không thể hoàn tác.</strong>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Phạm vi xoá</Form.Label>
            <Form.Check
              type="radio"
              id="del-scope-selected"
              name="delScope"
              label={`Chỉ ${selected.size} cửa hàng đã chọn`}
              checked={deleteScope === "selected"}
              onChange={() => setDeleteScope("selected")}
              disabled={selected.size === 0 || deleting}
            />
            <Form.Check
              type="radio"
              id="del-scope-all"
              name="delScope"
              label="Tất cả đơn WooCommerce của tôi"
              checked={deleteScope === "all"}
              onChange={() => setDeleteScope("all")}
              disabled={deleting}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="small fw-semibold">
              Gõ <code>DELETE</code> để xác nhận
            </Form.Label>
            <Form.Control
              type="text"
              autoFocus
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="DELETE"
              disabled={deleting}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={deleting}>
            Huỷ
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAll}
            disabled={deleting || deleteConfirm !== "DELETE"}
          >
            {deleting ? (
              <><Spinner size="sm" animation="border" className="me-2" />Đang xoá…</>
            ) : (
              <><FontAwesomeIcon icon={faTrash} className="me-2" />Xoá ngay</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default WooSyncOrders;
